import React from 'react';
import {
  Alert,
  AlertTitle,
  Snackbar,
  CircularProgress,
  Box,
  Typography
} from '@mui/material';
import { SubmissionStatus } from '../pages/forms/FormContext';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface SubmissionFeedbackProps {
  status: SubmissionStatus;
  error: string | null;
  onClose?: () => void;
}

const SubmissionFeedback: React.FC<SubmissionFeedbackProps> = ({ status, error, onClose }) => {
  // Não mostrar nada se estiver no estado inicial
  if (status === 'idle') {
    return null;
  }

  return (
    <>
      {/* Loading state */}
      {status === 'submitting' && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              backgroundColor: 'background.paper',
              p: 4,
              borderRadius: 2,
              boxShadow: 3,
              textAlign: 'center',
              maxWidth: 400,
            }}
          >
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6">Enviando formulário...</Typography>
            <Typography variant="body2" color="text.secondary">
              Por favor, aguarde enquanto salvamos suas informações.
            </Typography>
          </Box>
        </Box>
      )}

      {/* Success message */}
      {status === 'success' && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              backgroundColor: 'background.paper',
              p: 4,
              borderRadius: 2,
              boxShadow: 3,
              textAlign: 'center',
              maxWidth: 400,
            }}
          >
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h6">Formulário enviado com sucesso!</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Obrigado por fornecer as informações para o seu assistente virtual.
              Entraremos em contato em breve para os próximos passos.
            </Typography>
            {onClose && (
              <Typography 
                variant="body2" 
                color="primary" 
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
                onClick={onClose}
              >
                Fechar
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Error message */}
      {status === 'error' && (
        <Snackbar
          open={true}
          autoHideDuration={6000}
          onClose={onClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            severity="error"
            variant="filled"
            onClose={onClose}
            icon={<ErrorOutlineIcon />}
            sx={{ width: '100%' }}
          >
            <AlertTitle>Erro ao enviar formulário</AlertTitle>
            {error || 'Ocorreu um erro ao enviar o formulário. Por favor, tente novamente.'}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default SubmissionFeedback;